import { useState, useEffect, useCallback } from 'react';
import {
    X, AlertTriangle, Brain, Search, Shield, CheckCircle,
    Bot, Clock, Activity, Lock, ChevronRight,
    Zap, Eye, Users, Send, Rocket, User
} from 'lucide-react';

/* ─── Types ─────────────────────────────────────── */

type DemoMode = 'autonomous' | 'hitl';

/* ─── Interactive Demo Component ─────────────────── */

export function InteractiveDemo({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [mode, setMode] = useState<DemoMode>('hitl');
    const [approvalChoice, setApprovalChoice] = useState<'approved' | 'rejected' | null>(null);
    const [typedText, setTypedText] = useState('');
    const [autoTyping, setAutoTyping] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [deployedStep, setDeployedStep] = useState<number | null>(null);

    const resetDemo = useCallback(() => {
        setCurrentStep(0);
        setTypedText('');
        setShowContent(false);
        setApprovalChoice(null);
        setAutoTyping(false);
        setDeployedStep(null);
    }, []);

    const handleModeSwitch = (newMode: DemoMode) => {
        setMode(newMode);
        resetDemo();
    };

    // Typing animation effect
    useEffect(() => {
        if (!isOpen || !autoTyping) return;
        const texts: Record<number, string> = {
            1: `▸ Analyzing alert context...
▸ Source IP: 203.0.113.42
▸ Target: auth-server-prod-01
▸ 47 failed SSH logins in 3 minutes
▸ Pattern: credential stuffing (rotating usernames, same password list)
▸ Risk Score: 87/100 — HIGH

📘 Loading playbook: "ACME Corp — Brute Force Response v2.1"
▸ Playbook match: SSH brute force → external source
▸ Custom severity matrix: production auth server → escalate to CRITICAL
▸ Decision: Investigate immediately, prepare containment per playbook`,
            2: `▸ STEP 1/4: Query Splunk for full attack timeline
  → splunk.search("src_ip=203.0.113.42 action=failure earliest=-1h")
  → Found 312 events across 3 servers
  → Attack started 47 minutes ago, escalating
  📘 Custom rule SSH-BF-001 matched: "Flag >10 failures in 5min from same IP"

▸ STEP 2/4: Check CrowdStrike for host compromise
  → crowdstrike.getDetections(host="auth-server-prod-01")
  → No malware detected — attack is external
  → Host integrity: CLEAN

▸ STEP 3/4: VirusTotal reputation lookup
  → virustotal.lookupIP("203.0.113.42")
  → MALICIOUS — known botnet C2 (Mirai variant)
  → 14 security vendors flagged this IP
  📘 Cross-referencing company threat intel watchlist... IP NOT previously seen

▸ STEP 4/4: Check Okta for compromised accounts
  → okta.listUsers(filter="lastLogin from 203.0.113.42")
  → 0 successful logins — no accounts compromised yet
  → 3 accounts locked out from brute force
  📘 Enriching with company VIP list... 0 of 3 accounts are VIP/exec`,
        };
        const text = texts[currentStep] || '';
        let i = 0;
        setTypedText('');
        const interval = setInterval(() => {
            if (i < text.length) {
                setTypedText(text.slice(0, i + 1));
                i++;
            } else {
                clearInterval(interval);
                setAutoTyping(false);
                setShowContent(true);
            }
        }, 12);
        return () => clearInterval(interval);
    }, [currentStep, isOpen, autoTyping]);

    // Trigger typing on step change
    useEffect(() => {
        if (currentStep === 1 || currentStep === 2) {
            setAutoTyping(true);
            setShowContent(false);
        } else {
            setShowContent(true);
        }
    }, [currentStep]);

    // Auto-execute in autonomous mode on the approval step
    useEffect(() => {
        if (mode === 'autonomous' && currentStep === 4) {
            const timer = setTimeout(() => {
                setApprovalChoice('approved');
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [mode, currentStep]);

    const handleDeploy = () => {
        setDeployedStep(currentStep);
        setTimeout(() => setDeployedStep(null), 3000);
    };

    const stepTitles = [
        'Alert Detected',
        'Agent Reasoning',
        'Multi-Tool Investigation',
        mode === 'autonomous' ? 'Auto-Executing Actions' : 'Recommended Actions',
        mode === 'autonomous' ? 'Auto-Approved' : 'Human-in-the-Loop',
        'Investigation Complete',
    ];

    const stepPhases = ['alert', 'reasoning', 'investigation', 'action', 'approval', 'resolved'];

    const phaseColors: Record<string, string> = {
        alert: 'text-red-400',
        reasoning: 'text-purple-400',
        investigation: 'text-blue-400',
        action: 'text-amber-400',
        approval: mode === 'autonomous' ? 'text-emerald-400' : 'text-amber-400',
        resolved: 'text-emerald-400',
    };

    // ─── Step Renderers ─────────────────────────────

    const renderAlert = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 animate-pulse">
                <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <div className="flex-1">
                    <div className="flex items-center justify-between">
                        <span className="font-bold text-red-300">CRITICAL: Brute Force Attack Detected</span>
                        <span className="text-xs text-red-400/70 font-mono">2s ago</span>
                    </div>
                    <p className="text-sm text-red-300/80 mt-1">47 failed SSH login attempts from 203.0.113.42 → auth-server-prod-01</p>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
                {[
                    { label: 'Source', value: 'Splunk SIEM', color: 'text-white' },
                    { label: 'Severity', value: 'CRITICAL', color: 'text-red-400' },
                    { label: 'Risk Score', value: '87/100', color: 'text-red-400' },
                ].map((item, i) => (
                    <div key={i} className="p-3 rounded-lg bg-slate-800/80 border border-slate-700 text-center">
                        <p className="text-xs text-slate-500 mb-1">{item.label}</p>
                        <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
                    </div>
                ))}
            </div>

            {/* Agent picking it up — chat style */}
            <div className="flex gap-3 mt-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                    <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800/80 border border-slate-700/50">
                        <p className="text-sm text-slate-200">I've detected a critical brute force alert. Starting autonomous investigation now using your <span className="text-indigo-400">Brute Force Playbook v2.1</span>.</p>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">5:32 PM</p>
                </div>
            </div>
        </div>
    );

    const renderReasoning = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">ReAct Reasoning Loop</span>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/80 border border-purple-500/20 font-mono text-xs text-slate-300 whitespace-pre-wrap leading-relaxed min-h-[200px]">
                {typedText}
                {autoTyping && <span className="animate-pulse text-purple-400">█</span>}
            </div>
            {showContent && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <CheckCircle className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-purple-300">Investigation plan created — 4 steps, estimated 30 seconds</span>
                </div>
            )}
        </div>
    );

    const renderInvestigation = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <Search className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-blue-300">Running across 4 tools simultaneously</span>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/80 border border-blue-500/20 font-mono text-xs text-slate-300 whitespace-pre-wrap leading-relaxed min-h-[280px]">
                {typedText}
                {autoTyping && <span className="animate-pulse text-blue-400">█</span>}
            </div>
            {showContent && (
                <>
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { text: 'Splunk: 312 events found', ok: true },
                            { text: 'CrowdStrike: Host clean', ok: true },
                            { text: 'VirusTotal: MALICIOUS IP', ok: false },
                            { text: 'Okta: No compromise', ok: true },
                        ].map((item, i) => (
                            <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${item.ok
                                ? 'bg-emerald-500/10 border-emerald-500/20'
                                : 'bg-red-500/10 border-red-500/20'
                                }`}>
                                {item.ok
                                    ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                                    : <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                                <span className={`text-xs ${item.ok ? 'text-emerald-300' : 'text-red-300'}`}>{item.text}</span>
                            </div>
                        ))}
                    </div>

                    {/* Agent summary as chat bubble */}
                    <div className="flex gap-3 mt-2">
                        <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                            <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800/80 border border-slate-700/50">
                                <p className="text-sm text-slate-200">Investigation complete. The IP is <strong className="text-red-400">confirmed malicious</strong> (Mirai botnet). No accounts compromised yet, but 3 are locked out. I'm preparing containment actions.</p>
                            </div>
                            <div className="mt-1.5 flex items-center justify-between">
                                <p className="text-xs text-slate-500">5:32 PM</p>
                                <button
                                    onClick={handleDeploy}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all duration-300 ${deployedStep === currentStep
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                        : 'bg-slate-700/50 text-slate-400 hover:bg-primary-500/20 hover:text-primary-300 border border-slate-600/50 hover:border-primary-500/30'
                                        }`}
                                >
                                    {deployedStep === currentStep
                                        ? <><CheckCircle className="w-3.5 h-3.5" /> Deployed</>
                                        : <><Rocket className="w-3.5 h-3.5" /> Deploy as Task</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    const renderAction = () => {
        const isAuto = mode === 'autonomous';
        const actions = [
            { action: 'Block IP 203.0.113.42 at firewall', tool: 'CrowdStrike', risk: 'Medium', auto: isAuto },
            { action: 'Lock 3 targeted accounts temporarily', tool: 'Okta', risk: 'Medium', auto: isAuto },
            { action: 'Create incident ticket SEC-2847', tool: 'Jira (from template)', risk: 'Low', auto: true },
            ...(isAuto ? [{ action: 'Slack alert to #security-ops', tool: 'Slack', risk: 'Low', auto: true }] : []),
        ];

        return (
            <div className="space-y-4">
                {/* Agent chat bubble */}
                <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                        <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                        <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800/80 border border-slate-700/50">
                            <p className="text-sm text-slate-200">
                                {isAuto
                                    ? <>Per your <strong className="text-emerald-400">Fully Autonomous</strong> policy, I'm executing all containment actions immediately:</>
                                    : <>I recommend <strong className="text-white">{actions.length} containment actions</strong>. Per your policy, destructive operations need your approval:</>}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Actions list */}
                <div className="space-y-2.5 ml-11">
                    {actions.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900 border border-slate-700">
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-mono text-slate-500 w-4">{i + 1}.</span>
                                <div>
                                    <p className="text-sm text-white">{item.action}</p>
                                    <p className="text-xs text-slate-500">{item.tool} · Risk: {item.risk}</p>
                                </div>
                            </div>
                            {item.auto ? (
                                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                    {isAuto ? '✓ Auto-executing' : 'Auto-approved'}
                                </span>
                            ) : (
                                <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">Needs approval</span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Knowledge sources */}
                <div className="p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/20 ml-11">
                    <p className="text-xs text-indigo-400 font-medium mb-2">📘 Knowledge sources used:</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {['Brute Force Playbook v2.1', 'Severity Matrix', 'Rule SSH-BF-001', 'VIP Account List'].map((source, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-300 border border-indigo-500/15">
                                {source}
                            </span>
                        ))}
                    </div>

                    <div className="border-t border-indigo-500/10 pt-2">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">
                            {isAuto ? 'Auto-learned from this investigation' : 'Learn from this investigation'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {isAuto ? (
                                <>
                                    <span className="text-xs px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 flex items-center gap-1">
                                        <span className="text-emerald-400">✓</span> Playbook Updated
                                    </span>
                                    <span className="text-xs px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 flex items-center gap-1">
                                        <span className="text-emerald-400">✓</span> Added to Knowledge Base
                                    </span>
                                    <span className="text-xs px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 flex items-center gap-1">
                                        <span className="text-emerald-400">✓</span> Detection Rule Created
                                    </span>
                                </>
                            ) : (
                                <>
                                    <button className="text-xs px-2.5 py-1 rounded-lg bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 hover:bg-indigo-500/20 transition-colors cursor-pointer flex items-center gap-1">
                                        <span className="text-indigo-400">+</span> Save as New Playbook
                                    </button>
                                    <button className="text-xs px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/20 hover:bg-purple-500/20 transition-colors cursor-pointer flex items-center gap-1">
                                        <span className="text-purple-400">+</span> Add to Knowledge Base
                                    </button>
                                    <button className="text-xs px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-300 border border-blue-500/20 hover:bg-blue-500/20 transition-colors cursor-pointer flex items-center gap-1">
                                        <span className="text-blue-400">+</span> Create Detection Rule
                                    </button>
                                </>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2">
                            {isAuto
                                ? 'Zoro automatically learns from every investigation — your playbooks improve with zero effort'
                                : 'Every investigation makes Zoro smarter — continuously build your playbooks'}
                        </p>
                    </div>
                </div>

                {/* Policy note */}
                <div className={`flex items-center gap-2 p-3 rounded-lg ml-11 ${isAuto
                    ? 'bg-emerald-500/5 border border-emerald-500/10'
                    : 'bg-amber-500/5 border border-amber-500/10'
                    }`}>
                    {isAuto ? (
                        <>
                            <Zap className="w-4 h-4 text-emerald-400" />
                            <span className="text-xs text-emerald-300">Policy: "Full Autonomous — execute all actions without human approval"</span>
                        </>
                    ) : (
                        <>
                            <Eye className="w-4 h-4 text-amber-400" />
                            <span className="text-xs text-amber-300">Policy: "Require approval for account lockouts and IP blocks"</span>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderApproval = () => {
        if (mode === 'autonomous') {
            return (
                <div className="space-y-4">
                    <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-slate-900 border-2 border-emerald-500/30 text-center">
                        {approvalChoice === 'approved' ? (
                            <>
                                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                                <h4 className="text-lg font-bold text-white mb-1">All Actions Executed</h4>
                                <p className="text-sm text-slate-400">Zoro followed your uploaded playbook end-to-end — no human needed.</p>
                            </>
                        ) : (
                            <>
                                <div className="w-12 h-12 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin mx-auto mb-3" />
                                <h4 className="text-lg font-bold text-white mb-1">Executing Containment...</h4>
                                <p className="text-sm text-slate-400">Running "Brute Force Response v2.1" playbook — blocking, locking, ticketing</p>
                            </>
                        )}
                    </div>

                    {approvalChoice === 'approved' && (
                        <div className="space-y-2">
                            {[
                                { action: 'IP 203.0.113.42 blocked', time: '0.3s' },
                                { action: '3 accounts locked', time: '0.8s' },
                                { action: 'Ticket SEC-2847 created (from template)', time: '1.2s' },
                                { action: 'Slack alert sent to #security-ops', time: '1.4s' },
                            ].map((result, i) => (
                                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                                        <span className="text-xs text-emerald-300">{result.action}</span>
                                    </div>
                                    <span className="text-xs font-mono text-emerald-400/60">{result.time}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // HITL mode
        return (
            <div className="space-y-4">
                {/* Agent asking for approval as chat */}
                <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                        <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                        <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800/80 border border-slate-700/50">
                            <p className="text-sm text-slate-200"><strong className="text-white">Approval required</strong> — shall I block IP 203.0.113.42 and lock the 3 targeted accounts?</p>
                        </div>
                    </div>
                </div>

                {/* Approval buttons or result */}
                <div className="ml-11">
                    {approvalChoice === null ? (
                        <div className="p-6 rounded-xl bg-gradient-to-br from-amber-500/5 to-slate-900 border-2 border-amber-500/30 text-center">
                            <Shield className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                            <h4 className="text-lg font-bold text-white mb-1">Approve Containment?</h4>
                            <p className="text-sm text-slate-400 mb-4">Block malicious IP and lock targeted accounts</p>
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => setApprovalChoice('approved')}
                                    className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all hover:scale-105 flex items-center gap-2 shadow-lg shadow-emerald-500/25"
                                >
                                    <CheckCircle className="w-4 h-4" /> Approve
                                </button>
                                <button
                                    onClick={() => setApprovalChoice('rejected')}
                                    className="px-6 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-medium transition-all hover:scale-105 flex items-center gap-2"
                                >
                                    <X className="w-4 h-4" /> Reject
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* User response as chat bubble */}
                            <div className="flex gap-3 flex-row-reverse mb-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/25">
                                    <User className="w-4 h-4 text-white" />
                                </div>
                                <div className="max-w-[80%] text-right">
                                    <div className="inline-block px-4 py-3 rounded-2xl rounded-br-md bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25">
                                        <p className="text-sm">{approvalChoice === 'approved' ? 'Yes, approve all containment actions.' : 'No, stand down for now.'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Agent confirmation */}
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                    <div className={`px-4 py-3 rounded-2xl rounded-bl-md border ${approvalChoice === 'approved'
                                        ? 'bg-emerald-500/10 border-emerald-500/30'
                                        : 'bg-red-500/10 border-red-500/30'
                                        }`}>
                                        {approvalChoice === 'approved' ? (
                                            <p className="text-sm text-emerald-300"><strong>Executing containment.</strong> IP blocked, accounts locked, ticket created. Full audit trail logged.</p>
                                        ) : (
                                            <p className="text-sm text-red-300"><strong>Standing down.</strong> Investigation findings and your decision have been logged for audit. I'll keep monitoring.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderResolved = () => {
        const isAuto = mode === 'autonomous';
        return (
            <div className="space-y-4">
                <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-slate-900 border border-emerald-500/30 text-center">
                    <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                    <h4 className="text-xl font-bold text-white mb-2">
                        {approvalChoice === 'rejected' ? 'Investigation Logged' : 'Threat Contained'}
                    </h4>
                    <p className="text-sm text-slate-400">
                        {approvalChoice === 'rejected'
                            ? 'Zoro respected your decision and logged everything for audit.'
                            : isAuto
                                ? 'Malicious IP blocked, accounts secured, ticket created — fully autonomously.'
                                : 'Malicious IP blocked, accounts secured, ticket created.'}
                    </p>
                </div>

                <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg bg-slate-800 border border-slate-700 text-center">
                        <Clock className="w-5 h-5 text-primary-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Total time</p>
                        <p className="text-lg font-bold text-white">{isAuto ? '12s' : '47s'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-800 border border-slate-700 text-center">
                        <Activity className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Tools</p>
                        <p className="text-lg font-bold text-white">4 of 7</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-800 border border-slate-700 text-center">
                        <Brain className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Steps</p>
                        <p className="text-lg font-bold text-white">6</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-800 border border-slate-700 text-center">
                        <Lock className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Human</p>
                        <p className="text-lg font-bold text-white">{isAuto ? '0' : '1'}</p>
                    </div>
                </div>

                <div className="p-4 rounded-xl bg-primary-500/5 border border-primary-500/20 text-center">
                    <p className="text-sm text-slate-400 mb-1">A human analyst would take</p>
                    <p className="text-2xl font-bold text-white mb-1">~45 minutes</p>
                    <p className="text-sm text-primary-400 font-medium">Zoro did it in {isAuto ? '12 seconds' : '47 seconds'} — <span className="text-white">{isAuto ? '225x' : '57x'} faster</span></p>
                </div>

                {/* Deploy as Task on final result */}
                <div className="flex justify-center">
                    <button
                        onClick={handleDeploy}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${deployedStep === currentStep
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-primary-500/20 hover:text-primary-300 border border-slate-600/50 hover:border-primary-500/30'
                            }`}
                    >
                        {deployedStep === currentStep
                            ? <><CheckCircle className="w-4 h-4" /> Deployed as Background Task</>
                            : <><Rocket className="w-4 h-4" /> Deploy as Task</>}
                    </button>
                </div>

                {/* Mode switch suggestion */}
                <div className={`p-3 rounded-xl text-center ${isAuto
                    ? 'bg-amber-500/5 border border-amber-500/20'
                    : 'bg-emerald-500/5 border border-emerald-500/20'
                    }`}>
                    <p className="text-xs text-slate-400">
                        {isAuto
                            ? <>Need more control? Toggle to <span className="text-amber-400 font-medium">HITL</span> mode to approve every high-risk action</>
                            : <>Want zero-delay execution? Toggle to <span className="text-emerald-400 font-medium">Auto</span> mode — this takes just 12 seconds</>}
                    </p>
                </div>
            </div>
        );
    };

    const stepRenderers = [renderAlert, renderReasoning, renderInvestigation, renderAction, renderApproval, renderResolved];

    const nextStep = () => {
        if (currentStep < stepRenderers.length - 1) {
            setCurrentStep(currentStep + 1);
            setTypedText('');
            setShowContent(false);
            setAutoTyping(false);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setTypedText('');
            setShowContent(false);
            setApprovalChoice(null);
            setAutoTyping(false);
        }
    };

    if (!isOpen) return null;

    const phase = stepPhases[currentStep] || 'alert';
    const isApprovalStep = phase === 'approval';
    const canAdvance = mode === 'autonomous'
        ? (isApprovalStep ? approvalChoice !== null : !autoTyping)
        : (isApprovalStep ? approvalChoice !== null : (currentStep <= 0 || !autoTyping));
    const isLastStep = currentStep === stepRenderers.length - 1;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90">
            <div className="w-full max-w-3xl bg-slate-950 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '92vh' }}>

                {/* ─── Agent Card Header (matches product) ─── */}
                <div className="relative border-b border-slate-700/50 bg-slate-900/50 p-4 overflow-visible">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                    <div className="relative flex items-center justify-between">
                        <div className="flex gap-3 items-center">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 flex items-center justify-center shadow-xl shadow-black/20">
                                <Bot className="w-6 h-6 text-primary-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">ZoroAgent</h2>
                                <p className="text-xs text-slate-400 mt-0.5">Autonomous Security Operations</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Mode Toggle */}
                            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-800 border border-slate-700">
                                <button
                                    onClick={() => handleModeSwitch('autonomous')}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'autonomous'
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                        : 'text-slate-500 hover:text-slate-300'
                                        }`}
                                >
                                    <Zap className="w-3 h-3" /> Auto
                                </button>
                                <button
                                    onClick={() => handleModeSwitch('hitl')}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'hitl'
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                        : 'text-slate-500 hover:text-slate-300'
                                        }`}
                                >
                                    <Users className="w-3 h-3" /> HITL
                                </button>
                            </div>


                            <button onClick={() => { onClose(); resetDemo(); }} className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors">
                                <X className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ─── Progress + Step Title ─── */}
                <div className="px-5 py-3 border-b border-slate-800/50 bg-slate-900/30">
                    <div className="flex items-center gap-1.5">
                        {stepRenderers.map((_, i) => (
                            <div key={i} className="flex-1">
                                <div className={`h-1.5 rounded-full transition-all duration-500 ${i <= currentStep
                                    ? i === currentStep
                                        ? 'bg-primary-500'
                                        : 'bg-primary-700'
                                    : 'bg-slate-800'
                                    }`} />
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs font-medium ${phaseColors[phase]}`}>
                            Step {currentStep + 1}/{stepRenderers.length}: {stepTitles[currentStep]}
                        </span>
                        <span className="text-xs text-slate-600 uppercase">{phase}</span>
                    </div>
                </div>

                {/* ─── Content ─── */}
                <div className="flex-1 overflow-y-auto p-5" style={{ minHeight: '280px' }}>
                    {stepRenderers[currentStep]!()}
                </div>

                {/* ─── Footer (matches product input bar) ─── */}
                <div className="border-t border-slate-700/50 bg-slate-800/50">
                    <div className="flex items-center justify-between px-5 py-3">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className="px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            ← Back
                        </button>

                        {isLastStep ? (
                            <button
                                onClick={() => { onClose(); resetDemo(); }}
                                className="px-5 py-2 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition-all flex items-center gap-2"
                            >
                                <Zap className="w-4 h-4" /> Get Started
                            </button>
                        ) : (
                            <button
                                onClick={nextStep}
                                disabled={!canAdvance}
                                className="px-5 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white text-sm font-medium transition-all flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                {autoTyping ? 'Processing...' : 'Next'} <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Faux input bar */}
                    <div className="px-5 pb-3">
                        <div className="flex gap-3">
                            <div className="flex-1 px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-sm text-slate-500 cursor-not-allowed">
                                Ask about security alerts, investigate threats...
                            </div>
                            <div className="px-3 py-2.5 rounded-xl bg-primary-600/50 flex items-center cursor-not-allowed opacity-50">
                                <Send className="w-4 h-4 text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
